import { createPlaceholderNode, nodeHasChildren } from '@ui/lib/reactFlow/helpers'
import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { BrowserCodeIcon, HurricaneTwisterIcon, StartPointDiamondIcon } from '@ui/components/icons'
import { NodeTypeEnum } from '@ui/interfaces'
import { useNodeAndEdgeStore } from '@ui/stores'
import { useLayoutPanelManagerStore, useSaveUpdateStatusStore } from '@ui/stores/pipelinesWorkflow'

import { LayoutPanel, RIGHT_PANEL_CONTENT } from '../PipelineLayoutManager'
import { PanelContainer } from './Containers'

export const CreateNode = () => {
  const { updateNodeType, currentNodeId, edges, updateNodeKeyValue } = useNodeAndEdgeStore(
    useShallow((state) => ({
      updateNodeType: state.updateNodeType,
      currentNodeId: state.currentNodeId || '',
      edges: state.edges,
      updateNodeKeyValue: state.updateNodeKeyValue,
    }))
  )

  const { setPanelContent } = useLayoutPanelManagerStore(
    useShallow((state) => ({
      setPanelContent: state.setPanelContent,
    }))
  )

  const { setPipelineModified } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      setPipelineModified: state.setPipelineModified,
    }))
  )

  const hasChildNodes = useMemo(() => {
    return nodeHasChildren(edges, currentNodeId)
  }, [currentNodeId, edges])

  const steps = useMemo(() => {
    const steps = [
      {
        nodeType: NodeTypeEnum.TRANSFORM,
        title: 'Transformation',
        description: 'Holds multiple no-code steps for data processing',
        icon: () => <HurricaneTwisterIcon className="stroke-gray-900" />,
      },
      {
        nodeType: NodeTypeEnum.CODE,
        title: 'Code',
        description: 'Custom JavaScript code for data manipulation',
        icon: () => <BrowserCodeIcon className="stroke-gray-900" />,
      },
    ]
    // If current node has no children i.e end of the pipeline
    // Then it can possibily be a destination or export
    if (!hasChildNodes) {
      steps.push({
        nodeType: NodeTypeEnum.EXPORT,
        title: 'Destination',
        description: 'Specifies where processed data is sent or stored',
        icon: () => <StartPointDiamondIcon className="stroke-gray-900" />,
      })
    }

    return steps
  }, [hasChildNodes])

  const closeAllPanels = () => {
    setPanelContent({
      panel: LayoutPanel.RIGHT_PANEL,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      ignoreMaxConstraints: true,
      open: false,
    })
    // setPanelContent({
    //   panel: LayoutPanel.MIDDLE_PANEL,
    //   content: RIGHT_PANEL_CONTENT.TRANSFORMATION_STEPS,
    //   open: false,
    // })
  }

  const closeRightPanel = () => {
    setPanelContent({
      panel: LayoutPanel.RIGHT_PANEL,
      content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
      open: false,
    })
  }

  const onClick = (nodeType: NodeTypeEnum) => {
    switch (nodeType) {
      case NodeTypeEnum.CODE:
        closeAllPanels()
        updateNodeType(currentNodeId, nodeType)
        setPanelContent({
          panel: LayoutPanel.RIGHT_PANEL,
          content: RIGHT_PANEL_CONTENT.TRANSFORMATION_CODE,
          ignoreMaxConstraints: true,
          open: true,
        })
        createPlaceholderNode({ currentNodeId, undeletable: !hasChildNodes })
        break
      case NodeTypeEnum.TRANSFORM:
        closeAllPanels()
        updateNodeType(currentNodeId, nodeType)
        setPanelContent({
          panel: LayoutPanel.RIGHT_PANEL,
          content: RIGHT_PANEL_CONTENT.TRANSFORMATION_STEPS,
          open: true,
        })
        createPlaceholderNode({ currentNodeId, undeletable: !hasChildNodes })
        break
      case NodeTypeEnum.EXPORT:
        closeAllPanels()
        updateNodeType(currentNodeId, nodeType)
        updateNodeKeyValue(currentNodeId, {
          description: 'Pick a destination',
        })
        setPanelContent({
          panel: LayoutPanel.RIGHT_PANEL,
          content: RIGHT_PANEL_CONTENT.DESTINATIONS,
          open: true,
        })
        break
    }

    setPipelineModified(true)
  }

  return (
    <PanelContainer
      title="Add node"
      subtitle="Select the type of step you want to add to your pipeline. You can add multiple steps to your pipeline to transform your data."
      onClose={closeRightPanel}>
      <div className="overflow-hidden px-4">
        {steps.map((step) => (
          <div data-testid={step.nodeType} key={step.nodeType} onClick={() => onClick(step.nodeType)}>
            <div className="group relative mb-2 flex cursor-pointer items-center gap-x-6 rounded-lg border border-slate-300 bg-white p-4 text-sm leading-6 duration-100 ease-in hover:border-primary-600 hover:bg-primary-100">
              <div className="flex h-11 w-11 flex-none items-center justify-center rounded-lg border border-slate-300 bg-gray-50 group-hover:border-primary-600">
                {step.icon && <step.icon />}
              </div>
              <div className="flex-auto">
                <p className="block font-semibold">
                  {step.title}
                  <span className="absolute inset-0" />
                </p>
                <p className="mt-1 text-gray-600">{step.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </PanelContainer>
  )
}
